import { SidebarList, GlobalSidebar, FormInputAntd, FormSelectAntd, FormCheckbox, AntdButton } from "components"
import { yupResolver } from "@hookform/resolvers/yup"
import { useAppDispatch, useAppSelector } from "hooks"
import {
	getSDIncidents,
	isSDIncidentsLoading,
	isSDIncidentsSaving,
	saveSDIncidents,
	selectSDIncidentsById,
	selectSDIncidentsList,
} from "store/StaticData/SDIncidents.slice"
import { SDIncidentsInterface } from "@type/staticData.types"
import { useEffect, useMemo, useRef } from "react"
import { useForm, SubmitHandler, FormProvider } from "react-hook-form"
import { SDIncidentInitialValues, SDIncidentSchema } from "Schema/StaticData/SDIncidents"
import { selectDefaultEscalationType } from "config/constants"
import { useAdminQueryParams } from "hooks"
import { sortByProperty } from "config/utils"

interface SDIncidentsFormHandler extends SDIncidentsInterface {}

const SDIncidents = () => {
	const dispatch = useAppDispatch()
	const saving = useAppSelector(isSDIncidentsSaving())
	const loading = useAppSelector(isSDIncidentsLoading())
	const SDIncidentsList = useAppSelector(selectSDIncidentsList)
	const { params, setSearchParams } = useAdminQueryParams()
	const { selected: selectedSDIncident = "" } = params
	const SDIncidentsById = useAppSelector(selectSDIncidentsById(selectedSDIncident))
	const isNewBeingCreated = useRef(false)
	const methods = useForm<SDIncidentsFormHandler>({ resolver: yupResolver(SDIncidentSchema) })

	const {
		handleSubmit,
		reset,
		formState: { isDirty, isSubmitting, isSubmitted },
	} = methods

	const sidebarSDIncidents = useMemo(() => {
		return (SDIncidentsList ?? [])?.map(SDIncidents => ({
			_id: SDIncidents._id,
			name: SDIncidents.name,
		}))
	}, [saving, SDIncidentsList?.length, isSubmitted])

	useEffect(() => {
		dispatch(getSDIncidents())
	}, [])

	useEffect(() => {
		if (!selectedSDIncident && !!SDIncidentsList?.length && !isNewBeingCreated.current) {
			setSearchParams({ selected: SDIncidentsList?.[0]?._id })
		}
	}, [params, SDIncidentsList])

	useEffect(() => {
		if (selectedSDIncident && SDIncidentsById) {
			reset({ ...SDIncidentInitialValues, ...SDIncidentsById })
		} else if (!selectedSDIncident && !isNewBeingCreated.current) {
			reset({ ...SDIncidentInitialValues })
		}
	}, [selectedSDIncident, SDIncidentsById])

	const onSubmit: SubmitHandler<SDIncidentsFormHandler> = data => {
		isNewBeingCreated.current = true
		dispatch(
			saveSDIncidents(data, id => {
				setSearchParams({ selected: id })
				reset(data)
			}),
		)
	}

	const handleSelected = (SDIncidentsId: string): void => {
		setSearchParams({ selected: SDIncidentsId })
	}

	return (
		<>
			<GlobalSidebar>
				<SidebarList
					title={"Incidents"}
					loading={loading}
					selected={selectedSDIncident}
					setSelected={handleSelected}
					list={sidebarSDIncidents.sort(sortByProperty("name"))}
				/>
			</GlobalSidebar>
			<FormProvider {...methods}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<div className="grid grid-cols-4 gap-y-4">
						<div className="col-span-2 grid grid-cols-2 gap-x-4 gap-y-2">
							<FormInputAntd type="text" label="Short Desc" name="name" />
							<FormInputAntd type="text" label="Description" name="description" />

							<FormSelectAntd
								label="Default Escalation Type"
								name="defaultEscalationType"
								options={[
									{ label: "Select Escalation Type", value: "Select Escalation Type" },
									...selectDefaultEscalationType,
								]}
							/>
							<FormInputAntd type="text" label="Priority" name="priority" />
							<FormCheckbox label="Shown in WorkMonitor" name="shownInWorkMonitor" />
							<FormCheckbox label="Is Company Wide" name="isCompanyWide" />
							<FormCheckbox label="Incident Description Required" name="isDescriptionRequired" />
							<FormCheckbox label="Enabled" name="isEnabled" />
						</div>
					</div>
					<div className="flex space-x-3 py-5">
						<AntdButton
							onClick={() => {
								isNewBeingCreated.current = true
								reset({ ...SDIncidentInitialValues })
								setSearchParams({ selected: "" })
							}}
						>
							New
						</AntdButton>
						<AntdButton disabled={!isDirty || isSubmitting} loading={saving} htmlType="submit">
							{selectedSDIncident ? "Update" : "Save"}
						</AntdButton>
					</div>
				</form>
			</FormProvider>
		</>
	)
}

export default SDIncidents
